/**
 * A record off disk, turned into a profile or refused.
 *
 * One gate, in one order, and every load goes through it:
 *
 * 1. shape. A record that is not an object is not a profile.
 * 2. version. Newer than this build is refused outright, older is walked forward by the chain.
 * 3. sections. Each is handed to whoever owns its rules, and the first refusal ends the load.
 *
 * Refuses rather than repairs, at the level of the record. A save that names two items this build
 * no longer has does not load with those two items missing: silently dropping gear is how a player
 * loses a plate carrier and blames the netcode. Inside a section, a value that is merely out of
 * range is pulled back into range, because retuning a number is a thing this repo expects to do and
 * last week's save should survive it. Ids are refused, magnitudes are clamped.
 *
 * `settings` is the one section that refuses nothing at all. See `readSettings` for why a knob is
 * not worth a character.
 *
 * The chain is a parameter so tests can walk a fixture chain. Callers pass `SAVE_MIGRATIONS`.
 */

import { runMigrations } from '@/save/migrationChain';
import { asNumber, asRecord, asString } from '@/save/recordFields';
import { validateProgress } from '@/sim/progression/progressState';
import { SAVE_SCHEMA_VERSION } from '@/save/saveContract';
import { asStoredInventory } from '@/save/storedInventoryShape';
import { refused, saved } from '@/save/saveResult';
import { sanitizePlayerName } from '@/identity/playerNameSanitize';
import { BASELINE_STATS, clampStat, STAT_ORDER } from '@/sim/combat/statContract';
import { migrateInventory } from '@/sim/inventory/inventorySchema';
import type { SaveMigration } from '@/save/migrationContract';
import type { SaveProfile, StoredSettings } from '@/save/saveContract';
import type { SaveResult } from '@/save/saveResult';
import type { ProgressState } from '@/sim/progression/progressState';
import type { StatBlock, StatId } from '@/sim/combat/statContract';

/** What a name falls back to when a save carries one that sanitizes down to nothing. */
export const UNNAMED_PROFILE = 'Nameless';

export function readProfile(
  raw: unknown,
  chain: readonly SaveMigration[],
): SaveResult<SaveProfile> {
  const record = asRecord(raw, 'profile');
  if (!record.ok) return record;

  const version = asNumber(record.value.schemaVersion, 'profile.schemaVersion');
  if (!version.ok) return version;

  const migrated = runMigrations(record.value, version.value, SAVE_SCHEMA_VERSION, chain);
  if (!migrated.ok) return migrated;

  const current = migrated.value;

  const profileId = asString(current.profileId, 'profile.profileId');
  if (!profileId.ok) return profileId;
  if (profileId.value === '') return refused({ reason: 'malformed', field: 'profile.profileId' });

  const name = asString(current.name, 'profile.name');
  if (!name.ok) return name;

  const stats = readStats(current.stats);
  if (!stats.ok) return stats;

  const progress = readProgress(current.progress);
  if (!progress.ok) return progress;

  const stored = asStoredInventory(current.inventory, 'profile.inventory');
  if (!stored.ok) return stored;

  const inventory = migrateInventory(stored.value);
  if (!inventory.ok) {
    return refused({ reason: 'sectionRejected', section: 'inventory', failure: inventory.failure });
  }

  const settings = readSettings(current.settings);

  const createdAtMs = asNumber(current.createdAtMs, 'profile.createdAtMs');
  if (!createdAtMs.ok) return createdAtMs;

  const savedAtMs = asNumber(current.savedAtMs, 'profile.savedAtMs');
  if (!savedAtMs.ok) return savedAtMs;

  return saved({
    schemaVersion: SAVE_SCHEMA_VERSION,
    profileId: profileId.value,
    name: sanitizePlayerName(name.value) || UNNAMED_PROFILE,
    stats: stats.value,
    progress: progress.value,
    inventory: inventory.value,
    settings,
    createdAtMs: createdAtMs.value,
    savedAtMs: savedAtMs.value,
  });
}

/**
 * Every knob the player has moved, and the one section that cannot refuse a load.
 *
 * A save is refused when it names gear this build cannot honour, because silently losing a plate
 * carrier is unrecoverable. A setting is not that: the worst a knob this build cannot read costs is
 * a slider back on its default, and refusing a whole character over one would be the expensive
 * mistake. So a section that is not a record reads as no settings at all, and a value that is
 * neither a finite number nor a string is dropped rather than refused.
 *
 * Two shapes because a knob is either a magnitude or a named option: a HUD size is 1.15 and a HUD
 * style is `plain`, and storing the second as an index into a list somebody later reorders is how a
 * save comes to mean the wrong thing quietly. Whether a string names something this build offers is
 * not asked here, for the same reason an unknown key is not.
 *
 * Keys are not checked against the registry here. `save/` has no business knowing what a knob is,
 * and a key this build does not know is one tomorrow's build may want back:
 * `ui/settings/settingValues.ts` decides what a key means and ignores the rest.
 */
function readSettings(value: unknown): StoredSettings {
  const record = asRecord(value, 'profile.settings');
  if (!record.ok) return {};

  const settings: Record<string, number | string> = {};
  for (const key of Object.keys(record.value)) {
    const raw = record.value[key];
    if (typeof raw === 'string') settings[key] = raw;
    else {
      const stored = asNumber(raw, `profile.settings.${key}`);
      if (stored.ok) settings[key] = stored.value;
    }
  }
  return settings;
}

/**
 * Experience and perk ranks, handed to the section's owner in the section's own vocabulary.
 *
 * Only the shape is decided here: that `progress` is a record, that `xp` is a number and that the
 * ranks are numbers. What counts as a perk and what counts as a rank belongs to `sim/progression`,
 * which is why a refusal comes back as a `ProgressionFailure` carried whole rather than flattened
 * into a field path.
 */
function readProgress(value: unknown): SaveResult<ProgressState> {
  const record = asRecord(value, 'profile.progress');
  if (!record.ok) return record;

  const xp = asNumber(record.value.xp, 'profile.progress.xp');
  if (!xp.ok) return xp;

  const ranksRecord = asRecord(record.value.perkRanks, 'profile.progress.perkRanks');
  if (!ranksRecord.ok) return ranksRecord;

  const ranks: Record<string, number> = {};

  for (const key of Object.keys(ranksRecord.value)) {
    const rank = asNumber(ranksRecord.value[key], `profile.progress.perkRanks.${key}`);
    if (!rank.ok) return rank;
    ranks[key] = rank.value;
  }

  const progress = validateProgress(xp.value, ranks);

  if (!progress.ok) {
    return refused({
      reason: 'sectionRejected',
      section: 'progression',
      failure: progress.failure,
    });
  }

  return saved(progress.value);
}

/**
 * Every stat named in `STAT_ORDER`, in that order.
 *
 * A missing stat is a malformed record: a profile with three of four stats is not a character with
 * a default, it is a record this build cannot read. A stat that is present and out of range is
 * clamped, which is what `clampStat` documents itself as being there for.
 */
function readStats(value: unknown): SaveResult<StatBlock> {
  const record = asRecord(value, 'profile.stats');
  if (!record.ok) return record;

  // Seeded from baseline so the block is total for the type checker rather than by assertion.
  // Every entry is overwritten below or the read fails before it returns.
  const stats: Record<StatId, number> = { ...BASELINE_STATS };

  for (const stat of STAT_ORDER) {
    const raw = asNumber(record.value[stat], `profile.stats.${stat}`);
    if (!raw.ok) return raw;
    stats[stat] = clampStat(raw.value);
  }

  return saved(stats);
}
